import * as React from "react";
import { memo, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Building, Globe, Mail, Save, Tag, User } from "lucide-react";
import { LeadCustomFieldsSection } from "@/components/leads/LeadCustomFieldsSection";
import { LeadPhonesField } from "@/components/leads/LeadPhonesField";
import { useOrganizationUsers } from "@/hooks/admin/useOrganizationUsers";
import {
  entriesFromLegacyPhone,
  prepareForSave,
  primaryPhoneOf,
  splitCanonical,
  validateLeadPhone,
} from "@/lib/leads/leadPhones";

const PhoneEntrySchema = z.object({
  value: z.string(),
  label: z.string(),
  is_primary: z.boolean(),
});

const LeadSchema = z.object({
  name: z.string().min(1, "Nome é obrigatório"),
  email: z
    .string()
    .optional()
    .or(z.literal(""))
    .refine((val) => !val || val.trim() === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim()), "Email inválido"),
  /** Lista editável. É a fonte de verdade do formulário desde a Fase 3. */
  phones: z.array(PhoneEntrySchema).optional().default([]),
  // `phone`/`phone_country_code` não são mais campos da tela: `handleSubmit` os deriva do
  // principal. Continuam no shape porque LeadCreate, LeadEdit, LeadCreateModal e
  // LinkLeadToDealModal concatenam os dois para gravar o espelho `leads.phone`.
  phone: z.string().optional().or(z.literal("")),
  phone_country_code: z.string().optional().default("55"),
  source: z.string().optional().or(z.literal("")),
  companyName: z.string().optional().or(z.literal("")),
  website: z.string().optional().or(z.literal("")),
  industry: z.string().optional().or(z.literal("")),
  notes: z.string().optional().or(z.literal("")),
      owner_id: z.string().optional(),
  // Valores podem ser string ou undefined (campos não preenchidos); obrigatoriedade é por campo nas configs
  custom_data: z.record(z.union([z.string(), z.undefined()])).optional(),
}).superRefine((data, ctx) => {
  // Valida cada entrada pela MESMA regra que `lead_phones` aplica (faixa canônica 12–13), em vez
  // da checagem própria que existia aqui. Entrada vazia é "ainda não preenchida" e some no
  // `prepareForSave` — só o que tem conteúdo e não é gravável bloqueia o submit.
  const invalid = (data.phones ?? []).filter((entry) => entry.value?.trim() && validateLeadPhone(entry.value));
  if (invalid.length > 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['phones'],
      message: invalid.length === 1
        ? "Há um telefone inválido — corrija ou remova antes de salvar."
        : `Há ${invalid.length} telefones inválidos — corrija ou remova antes de salvar.`,
    });
  }
});

export type LeadFormValues = z.infer<typeof LeadSchema>;

const FORM_STORAGE_KEY = "lead-form-draft";

export const LeadForm = memo(function LeadForm({
  defaultValues,
  onSubmit,
  submitLabel = "Salvar",
  submitting = false,
}: {
  defaultValues?: Partial<LeadFormValues>;
  onSubmit: (values: LeadFormValues) => Promise<void> | void;
  submitLabel?: string;
  submitting?: boolean;
}) {
  const { data: organizationUsers = [] } = useOrganizationUsers();
  const hasRestoredRef = useRef(false);
  const isSubmittingRef = useRef(false);
  
  // Restaurar dados salvos do sessionStorage ou usar defaultValues
  const getInitialValues = (): Partial<LeadFormValues> => {
    // Se há defaultValues explícitos (edit mode), usar eles
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      return {
        phone_country_code: "55",
        ...defaultValues,
        // Chamador que ainda passa só `phone` (LinkLeadToDealModal) abre com a lista preenchida.
        phones: defaultValues.phones?.length
          ? defaultValues.phones
          : entriesFromLegacyPhone(defaultValues.phone),
      };
    }
    
    // Tentar restaurar do sessionStorage
    try {
      const saved = sessionStorage.getItem(FORM_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Verificar se os dados salvos são recentes (últimos 30 minutos)
        if (parsed.timestamp && (Date.now() - parsed.timestamp) < 30 * 60 * 1000) {
          return parsed.values;
        }
      }
    } catch (error) {
      console.error('Erro ao restaurar formulário:', error);
    }
    
    // Valores padrão vazios
    return {
      name: "",
      email: "",
      phones: [],
      phone: "",
      phone_country_code: "55",
      source: "",
      companyName: "",
      website: "",
      industry: "",
      notes: "",
      owner_id: "",
      custom_data: {},
    };
  };

  const form = useForm<LeadFormValues>({
    resolver: zodResolver(LeadSchema),
    defaultValues: getInitialValues(),
  });

  // Restaurar valores salvos quando o formulário estiver pronto
  useEffect(() => {
    if (hasRestoredRef.current) return;
    
    const saved = sessionStorage.getItem(FORM_STORAGE_KEY);
    if (saved && !defaultValues) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.timestamp && (Date.now() - parsed.timestamp) < 30 * 60 * 1000) {
          // Restaurar valores salvos
          Object.entries(parsed.values).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== "") {
              form.setValue(key as keyof LeadFormValues, value as any);
            }
          });
          console.log('✅ [LeadForm] Valores restaurados do sessionStorage');
        }
      } catch (error) {
        console.error('Erro ao restaurar valores:', error);
      }
    }
    hasRestoredRef.current = true;
  }, [form, defaultValues]);

  // Auto-save: salvar valores no sessionStorage quando mudarem (com debounce)
  useEffect(() => {
    if (isSubmittingRef.current) return;
    
    let saveTimeout: NodeJS.Timeout;
    
    const subscription = form.watch((values) => {
      // Debounce: aguardar 500ms após a última mudança antes de salvar
      clearTimeout(saveTimeout);
      
      saveTimeout = setTimeout(() => {
        // Não salvar se estiver vazio (formulário recém criado)
        const hasAnyValue = Object.entries(values).some(([key, val]) => {
          if (typeof val === 'object' && val !== null) {
            return Object.keys(val).length > 0;
          }
          return val !== undefined && val !== null && val !== "";
        });
        
        if (hasAnyValue) {
          try {
            sessionStorage.setItem(FORM_STORAGE_KEY, JSON.stringify({
              values,
              timestamp: Date.now()
            }));
          } catch (error) {
            console.error('Erro ao salvar formulário:', error);
          }
        }
      }, 500); // 500ms de debounce
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(saveTimeout);
    };
  }, [form]);

  // Limpar dados salvos quando o formulário for submetido com sucesso
  const handleSubmit = async (values: LeadFormValues) => {
    isSubmittingRef.current = true;
    try {
      console.log('🔍 [LeadForm] Submitting values:', values);
      console.log('🔍 [LeadForm] Custom data:', values.custom_data);
      // Aplica dedupe + um principal só ANTES de entregar ao chamador, para o que ele manda para
      // `set_lead_phones` e o que ele grava em `leads.phone` saírem do mesmo conjunto.
      const phones = prepareForSave(values.phones ?? []);
      const { countryCode, national } = splitCanonical(primaryPhoneOf(phones));
      // Convert __none__ or empty string to undefined for owner_id
      const cleanedVals = {
        ...values,
        phones,
        phone: national,
        phone_country_code: countryCode,
        owner_id: (values.owner_id === "" || values.owner_id === "__none__") ? undefined : values.owner_id
      };
      await onSubmit(cleanedVals);
      // Limpar dados salvos após submit bem-sucedido
      sessionStorage.removeItem(FORM_STORAGE_KEY);
      hasRestoredRef.current = false;
    } catch (error) {
      // Se houver erro, manter os dados salvos
      console.error('Erro ao submeter formulário:', error);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {/* Duas colunas de 2/3 num grid de 3 deixavam um terço da linha vazio (e comprimiam
            os campos): agora "Informações Pessoais" ocupa a linha inteira e a segunda linha é
            Empresa (2/3) + Notas (1/3), sem sobra. */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-3">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Informações Pessoais
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField control={form.control} name="name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome *</FormLabel>
                    <FormControl>
                      <Input placeholder="Nome completo" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="email" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                        <Input className="pl-10" type="email" placeholder="email@empresa.com" {...field} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              {/* Repetidor de telefones: linha inteira. Meia coluna não cabia estrela + input +
                  rótulo + remover, e o input era o único elemento flexível — sobrava ~30px. */}
              <div className="grid grid-cols-1 gap-4">
                <FormField control={form.control} name="phones" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefones</FormLabel>
                    <FormControl>
                      <LeadPhonesField
                        value={field.value ?? []}
                        onChange={field.onChange}
                        disabled={submitting}
                      />
                    </FormControl>
                    <FormDescription>
                      O primeiro da lista, ou o marcado com a estrela, é o principal — é ele que a
                      discadora tenta primeiro e o que aparece na listagem.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField control={form.control} name="source" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fonte</FormLabel>
                    <FormControl>
                      <SearchableSelect
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder="Como chegou até nós?"
                        options={[
                          { value: "linkedin", label: "LinkedIn" },
                          { value: "website", label: "Website" },
                          { value: "cold-email", label: "Cold Email" },
                          { value: "referral", label: "Indicação" },
                          { value: "event", label: "Evento" },
                          { value: "social-media", label: "Redes Sociais" },
                          { value: "other", label: "Outro" },
                        ]}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="owner_id" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Proprietário</FormLabel>
                    <FormControl>
                      <SearchableSelect
                        value={field.value || "__none__"}
                        onValueChange={(value) => {
                          field.onChange(value === "__none__" ? undefined : value);
                        }}
                        placeholder="Selecione o vendedor responsável"
                        options={[
                          { value: "__none__", label: "Sem proprietário" },
                          ...organizationUsers.map((user) => ({
                            value: user.user_id,
                            label: user.full_name || user.email,
                          })),
                        ]}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Empresa
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField control={form.control} name="companyName" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome da Empresa</FormLabel>
                    <FormControl>
                      <Input placeholder="Digite o nome da empresa" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="website" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Website</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                        <Input className="pl-10" placeholder="https://empresa.com" {...field} />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="grid grid-cols-1 gap-4">
                <FormField control={form.control} name="industry" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Setor</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: Tecnologia" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </CardContent>
          </Card>

          <Card className="flex flex-col">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5" />
                Notas
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1">
              <FormField control={form.control} name="notes" render={({ field }) => (
                <FormItem className="flex h-full flex-col">
                  <FormLabel>Notas</FormLabel>
                  <FormControl className="flex-1">
                    <Textarea
                      className="h-full min-h-[96px] resize-y"
                      placeholder="Observações sobre o lead"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </CardContent>
          </Card>
        </div>

        {/* Seção de Campos Personalizados Organizacionais */}
        <div className="space-y-4 pt-4 border-t">
          <div>
            <h3 className="text-lg font-semibold">Informações Adicionais</h3>
            <p className="text-sm text-muted-foreground">Campos personalizados configurados para leads</p>
          </div>
          <LeadCustomFieldsSection
            customData={form.watch("custom_data")}
            onCustomDataChange={(customData) => {
              console.log('🔍 [LeadForm] Custom data changed:', customData);
              form.setValue("custom_data", customData);
            }}
          />
        </div>

        <div className="flex justify-end gap-4">
          <Button type="submit" disabled={submitting} className="gap-2">
            <Save className="h-4 w-4" />
            {submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
});
